import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Manuscript } from '../models/manuscript';
import { MsImageLinkList } from '../models/ms-image-link';
import { ImageLinkTree, ImageLinkTreeFuncs, MsTrees, ImageLinkTreeNode } from '../models/image-link-tree';
import { ManuscriptsService } from '../services/manuscripts.service';
import { VocsService } from '../services/vocs.service';
import { VocTerms, VocTerm } from '../models/voc-term';
import { VocTermByIdPipe } from '../pipes/voc-term-by-id.pipe';
import { environment } from '../../environments/environment';

export enum sifaIdPrefix {
    sections = 's_',
    parts = 'p_',
    treatises = 't_',
    chapters = 'c_',
    otherElements = 'o_'
}

@Injectable()
export class ImageLinkTreeService {
    private httpHeaders: HttpHeaders;
    private sifaSectionTree$: BehaviorSubject<ImageLinkTree> = new BehaviorSubject<ImageLinkTree>([]);
    private sifaOtherElementTree$: BehaviorSubject<ImageLinkTree> = new BehaviorSubject<ImageLinkTree>([]);
    private manuscript: Manuscript;
    private imageLinks: MsImageLinkList;
    private sifaSections: VocTerms;
    private sifaParts: VocTerms;
    private sifaTreatises: VocTerms;
    private sifaChapters: VocTerms;
    private sifaOtherElements: VocTerms;
    private childrenImageLinks: Array<MsImageLinkList> = [];

    constructor(
        private http: HttpClient,
        private manuscriptsService: ManuscriptsService,
        private vocsService: VocsService,
        private vocTermByIdPipe: VocTermByIdPipe
    ) {
        this.httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': environment.apiKey,
            'X-PhiBor-App': environment.phiborApp
        });

        combineLatest([
            this.vocsService.getSifaSections(),
            this.vocsService.getSifaParts(),
            this.vocsService.getSifaTreatises(),
            this.vocsService.getSifaChapters(),
            this.vocsService.getSifaOtherElements(),
            this.manuscriptsService.getManuscriptImageLinks(),
            this.manuscriptsService.getManuscript()
        ])
            .subscribe((response: Array<VocTerms | MsImageLinkList | Manuscript>) => {
                this.sifaSections = <VocTerms>response[0];
                this.sifaParts = <VocTerms>response[1];
                this.sifaTreatises = <VocTerms>response[2];
                this.sifaChapters = <VocTerms>response[3];
                this.sifaOtherElements = <VocTerms>response[4];
                this.imageLinks = <MsImageLinkList>response[5];
                this.manuscript = <Manuscript>response[6];

                if (this.sifaSections.length > 0
                    && this.sifaParts.length > 0
                    && this.sifaTreatises.length > 0
                    && this.sifaChapters.length > 0
                    && this.sifaOtherElements.length > 0
                    && this.imageLinks.length > 0
                    && this.manuscript
                ) {
                    /* Check if main manuscript has linked children */
                    if (this.manuscript.__children && this.manuscript.__children.length > 0) {
                        const childrenCombineLatest = [];
                        const childrenLength = this.manuscript.__children.length;

                        /* Build image link list API calls for every manuscript child */
                        for (let i = 0; i < childrenLength; i++) {
                            const childId = this.manuscript.__children[i];
                            childrenCombineLatest[i] = this.http.get<MsImageLinkList>(
                                environment.endpoints.de + '/manuscripts/' + childId + '/image-links/all',
                                {
                                    headers: this.httpHeaders,
                                    params: new HttpParams()
                                        .set('fields', 'image,pkid,fkidimg_files,fkidgridstruct_sections,fkidgridstruct_parts'
                                            + ',fkidgridstruct_treatises,fkidgridstruct_chapters,fkidms_structure'
                                            + ',fkidmanuscripts,version')
                                        .set('sort', 'image')
                                });
                        }

                        /* Get image link list for every manuscript child before load trees */
                        combineLatest(childrenCombineLatest)
                            .subscribe((childrenRes: Array<MsImageLinkList>) => {
                                for (let i = 0; i < childrenLength; i++) {
                                    this.childrenImageLinks[i] = <MsImageLinkList> childrenRes[i];
                                }
                                /* Load main manuscript trees */
                                this.loadTrees();
                            },
                                err => console.error(err)
                            );
                    } else {
                        /* Load main manuscript trees */
                        this.loadTrees();
                    }
                }
            },
            err => console.error(err));
    }

    private loadTrees(): void {
        /* Build main manuscript trees */
        const msTrees = this.buildTrees(this.imageLinks);

        /* Build children trees */
        for (let i = 0; i < this.childrenImageLinks.length; i++) {
            const msChildImageLinks = this.childrenImageLinks[i];
            if (msChildImageLinks.length > 0) {
                const msChildTrees = this.buildTrees(msChildImageLinks, 'd' + i + '_');
                this.mergeImageLinkTrees(msTrees.sifaSections, msChildTrees.sifaSections);
                this.mergeImageLinkTrees(msTrees.sifaOtherElements, msChildTrees.sifaOtherElements);
            }
        }

        /* Update observables */
        this.sifaSectionTree$.next(msTrees.sifaSections);
        this.sifaOtherElementTree$.next(msTrees.sifaOtherElements);
    }

    private buildTrees(imageLinks: MsImageLinkList, treeIdPrefix: string = ''): MsTrees {
        const msTrees = new MsTrees();
        if (imageLinks) {

            /* Populate the trees */
            for (let imageLinkPos = 0; imageLinkPos !== imageLinks.length; imageLinkPos++) {
                const imageLink = imageLinks[imageLinkPos];
                if (
                    imageLink.fkidgridstruct_sections !== 0
                    && imageLink.fkidgridstruct_parts !== 0
                    && imageLink.fkidgridstruct_treatises !== 0
                    && imageLink.fkidgridstruct_chapters !== 0
                ) {
                    const sifaSectionVocTerm = <VocTerm> this.vocTermByIdPipe.transform(
                        this.sifaSections, imageLink.fkidgridstruct_sections, false, true
                    );
                    const sifaSection = ImageLinkTreeFuncs.addNodeIfNotExists(
                        msTrees.sifaSections,
                        treeIdPrefix + sifaIdPrefix.sections + sifaSectionVocTerm.pkid,
                        sifaSectionVocTerm.term_arabic
                            ? sifaSectionVocTerm.term_arabic + ' (' + sifaSectionVocTerm.term + ')'
                            : sifaSectionVocTerm.term,
                        sifaSectionVocTerm.sorting
                    );
                    const sifaPartVocTerm = <VocTerm> this.vocTermByIdPipe.transform(
                        this.sifaParts, imageLink.fkidgridstruct_parts, false, true
                    );
                    const sifaPart = ImageLinkTreeFuncs.addNodeIfNotExists(
                        sifaSection.children,
                        treeIdPrefix + sifaIdPrefix.parts + sifaPartVocTerm.pkid,
                        sifaPartVocTerm.term,
                        sifaPartVocTerm.sorting
                    );
                    const sifaTreatiseVocTerm = <VocTerm> this.vocTermByIdPipe.transform(
                        this.sifaTreatises, imageLink.fkidgridstruct_treatises, false, true
                    );
                    const sifaTreatise = ImageLinkTreeFuncs.addNodeIfNotExists(
                        sifaPart.children,
                        treeIdPrefix + sifaIdPrefix.treatises + sifaTreatiseVocTerm.pkid,
                        sifaTreatiseVocTerm.term,
                        sifaTreatiseVocTerm.sorting
                    );
                    const sifaChapterVocTerm = <VocTerm> this.vocTermByIdPipe.transform(
                        this.sifaChapters, imageLink.fkidgridstruct_chapters, false, true
                    );
                    const sifaChapter = ImageLinkTreeFuncs.addNodeIfNotExists(
                        sifaTreatise.children,
                        treeIdPrefix + sifaIdPrefix.chapters + sifaChapterVocTerm.pkid,
                        sifaChapterVocTerm.term,
                        sifaChapterVocTerm.sorting
                    );
                    if (imageLink.__useRights === 1) { // link only public images
                        sifaChapter.links.push(imageLink.image);
                        sifaChapter.linkNode = true;
                    }

                } else if (imageLink.fkidgridstruct_sections > 0) { // introduction or similar
                    const sifaSectionVocTerm = <VocTerm> this.vocTermByIdPipe.transform(
                        this.sifaSections, imageLink.fkidgridstruct_sections, false, true
                    );
                    const sifaSection = ImageLinkTreeFuncs.addNodeIfNotExists(
                        msTrees.sifaSections,
                        treeIdPrefix + sifaIdPrefix.sections + sifaSectionVocTerm.pkid,
                        sifaSectionVocTerm.term,
                        sifaSectionVocTerm.sorting
                    );
                    if (imageLink.__useRights === 1) { // link only public images
                        sifaSection.links.push(imageLink.image);
                        sifaSection.linkNode = true;
                    }

                } else if (imageLink.fkidms_structure !== 0) { // other elements
                    const sifaOtherElementVocTerm = <VocTerm> this.vocTermByIdPipe.transform(
                        this.sifaOtherElements, imageLink.fkidms_structure, true, true
                    );
                    const sifaOtherElement = ImageLinkTreeFuncs.addNodeIfNotExists(
                        msTrees.sifaOtherElements,
                        treeIdPrefix + sifaIdPrefix.otherElements + sifaOtherElementVocTerm.pkid,
                        sifaOtherElementVocTerm.term_norm,
                        sifaOtherElementVocTerm.sorting
                    );
                    if (imageLink.__useRights === 1) { // link only public images
                        sifaOtherElement.links.push(imageLink.image);
                        sifaOtherElement.linkNode = true;
                    }

                }
            }

            /* Sort the trees */
            this.sortTree(msTrees.sifaSections);
            this.sortTree(msTrees.sifaOtherElements);
        }
        return msTrees;
    }

    private treeSortingFunc(a: ImageLinkTreeNode, b: ImageLinkTreeNode): number {
        if (a.pos === b.pos) {
            return 0;
        } else {
            return (a.pos < b.pos) ? -1 : 1;
        }
    }

    private sortTree(tree: ImageLinkTree): void {
        tree.sort(this.treeSortingFunc);
        for (let i = 0; i < tree.length; i++) {
            if (tree[i].children && tree[i].children.length > 0) {
                this.sortTree(tree[i].children);
            }
        }
    }

    private mergeImageLinkTrees(mainTree: ImageLinkTree, joinTree: ImageLinkTree): void {

        /* Scan main tree */
        for (let i = 0; i !== mainTree.length; i++) {
            const mainBranch = mainTree[i];
            const joinBranch = joinTree.find(branch => branch.name === mainBranch.name);
            if (joinBranch) {
                /* Recursive tree scan */
                if (mainBranch.children && mainBranch.children.length > 0
                    && joinBranch.children && joinBranch.children.length > 0) {
                    this.mergeImageLinkTrees(mainBranch.children, joinBranch.children);
                }

                /* Add corresponding duplicates links to the main branch */
                if (mainBranch.linkNode && joinBranch.links && joinBranch.links.length > 0) {

                    /* If the main node has no children, it is copied as the first child */
                    if (!mainBranch.children || mainBranch.children.length === 0) {
                        const mainBranchCopy = new ImageLinkTreeNode();
                        mainBranchCopy.id = 'm_' + mainBranch.id;
                        mainBranchCopy.name = mainBranch.name;
                        mainBranchCopy.links = mainBranch.links;
                        /* Main node update */
                        mainBranch.name += ' *';
                        mainBranch.children = [
                            mainBranchCopy
                        ];
                        delete mainBranch.links;
                    }

                    /* Add duplicate as child */
                    joinBranch.name += ' (dupl.)';
                    mainBranch.children.push(joinBranch);
                }
            }
        }
    }

    /* Get the image link tree of Sifa sections */
    getSifaSectionTree(): Observable<ImageLinkTree> {
        return this.sifaSectionTree$.asObservable();
    }

    /* Get the image link tree of Sifa sections */
    getSifaOtherElementTree(): Observable<ImageLinkTree> {
        return this.sifaOtherElementTree$.asObservable();
    }

}



// WEBPACK FOOTER //
// ./src/app/services/image-link-tree.service.ts